import { useEffect, useRef, useState } from "react";
import { getTigerResource, tigerFormat } from "../../utils/tigerResource";
import { formatTigerDateTime } from "../../utils/tigerTime";
import { acrylicStyles, acrylicTokens } from "../../theme/acrylic";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

interface ExportProgress {
  config: string;
  turbine: string;
  percent: number;
  /** Export window and the timestamp the server has reached, in Tiger seconds. */
  start_time: number;
  current_time: number;
  end_time: number;
  /** Trend export → show dates only (trend data is coarse). */
  is_trend: boolean;
}

interface ExportFinished {
  config: string;
  turbine: string;
  success: boolean;
  filename: string;
  message: string;
}

/**
 * Shown while a data/trend export is in flight. Opens on the first
 * `export-progress` event (from `DATA_SERVER_PROGRESS`), advances the bar as
 * more arrive, and closes on `export-finished` (the
 * `REMOTE_DATA_SERVER_EXPORT_*` reply). On success it offers to open the written
 * file; on failure it surfaces the server's error. The Cancel button issues
 * `DATA_SERVER_CANCEL` via the `cancel_export` command. Mirrors the legacy
 * `Ui_progress_info_dialog` + `Ti_store_data_server_*_reply` success prompt.
 *
 * Styled to match `GraphChannelSelectionDialog` (header / card body / footer).
 *
 * Self-contained: manages its own visibility, so it only needs mounting once.
 */
export function ExportProgressDialog() {
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  // Set when the user cancels so the trailing `export-finished` event doesn't
  // pop a success/error prompt for an export they deliberately aborted.
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!isTauri()) return;
    let cancelled = false;
    const offs: Array<() => void> = [];

    void import("@tauri-apps/api/event").then(async ({ listen }) => {
      const offProgress = await listen<ExportProgress>("export-progress", (e) => {
        setProgress(e.payload);
      });
      if (cancelled) offProgress();
      else offs.push(offProgress);

      const offFinished = await listen<ExportFinished>("export-finished", async (e) => {
        const f = e.payload;
        setProgress(null);
        // User cancelled: the dialog is already closed — don't prompt for an
        // export they deliberately aborted.
        if (cancelledRef.current) {
          cancelledRef.current = false;
          return;
        }
        // Use Tauri's native dialogs — window.confirm/alert are no-ops in the
        // webview, so the prompt would never appear.
        const { ask, message } = await import("@tauri-apps/plugin-dialog");
        if (f.success && f.filename) {
          const prompt = (getTigerResource("TIGER_EXPORT_SUCCESS_MSG") || "Export complete:\n%s\n\nOpen the file now?").replace(
            "%s",
            f.filename,
          );
          const doOpen = await ask(prompt, {
            title: getTigerResource("TIGER_EXPORT_BIN_TITLE") || "Export Complete",
            kind: "info",
          });
          if (doOpen) {
            try {
              const { open: shellOpen } = await import("@tauri-apps/plugin-shell");
              await shellOpen(f.filename);
            } catch (err) {
              // Don't fail silently — tell the user the file was written but
              // couldn't be opened, and where to find it.
              console.error("open exported file failed:", err);
              await message(tigerFormat(getTigerResource("TIGER_EXPORT_OPEN_FAILED"), f.filename, String(err)), {
                title: getTigerResource("TIGER_EXPORT_BIN_TITLE") || "Export",
                kind: "warning",
              });
            }
          }
        } else if (!f.success) {
          await message(f.message || getTigerResource("TIGER_ERROR_DATA_SERVER") || "Export failed.", {
            title: getTigerResource("TIGER_EXPORT_BIN_TITLE") || "Export",
            kind: "error",
          });
        }
      });
      if (cancelled) offFinished();
      else offs.push(offFinished);
    });

    return () => {
      cancelled = true;
      offs.forEach((off) => off());
    };
  }, []);

  if (!progress) return null;

  const pct = Math.max(0, Math.min(100, progress.percent));
  const title = getTigerResource("TIGER_EXPORT_BIN_TITLE") || "Exporting Tiger Data";

  // Split "HH:MM:SS DD/MM/YYYY" into the headline value (primary) and the
  // supporting line (secondary). Trend exports are coarse, so show dates only:
  // the date becomes the headline and there's no second line.
  const fmt = (secs: number): { primary: string; secondary: string } => {
    if (!secs) return { primary: "—", secondary: "" };
    const [time, date] = formatTigerDateTime(secs).split(" ");
    return progress.is_trend ? { primary: date, secondary: "" } : { primary: time, secondary: date };
  };
  const start = fmt(progress.start_time);
  const current = fmt(progress.current_time);
  const end = fmt(progress.end_time);

  const onCancel = async () => {
    const config = progress.config;
    cancelledRef.current = true;
    setProgress(null); // close the progress dialog immediately
    if (!isTauri()) return;
    const { invoke } = await import("@tauri-apps/api/core");
    // Still tell the data server to abort the in-flight export.
    await invoke("cancel_export", { configName: config });
  };

  return (
    <div style={styles.overlay} role="dialog" aria-modal="true" aria-label={title}>
      <div style={styles.dialog}>
        {/* Header */}
        <div style={styles.header}>
          <span style={styles.title}>{title}</span>
          <button
            className="acrylic-close-btn"
            onClick={onCancel}
            title={getTigerResource("TIGER_EXPORT_CANCEL_TOOLTIP")}
          >
            &#x2715;
          </button>
        </div>

        {/* Body */}
        <div style={styles.body}>
          <div style={styles.card}>
            <div style={styles.label}>
              {getTigerResource("TIGER_EXPORT_DATA_FOR")} <b>{progress.turbine || progress.config}</b>&hellip;
            </div>
            <div style={styles.barOuter}>
              <div style={{ ...styles.barInner, width: `${pct}%` }} />
              <div style={styles.barLabel}>{pct.toFixed(0)}%</div>
            </div>
            {/* Timeline: the export sweeps Start → End, with Current as the
                live position highlighted in the middle. */}
            <div style={styles.timeline}>
              <div style={styles.timeCol}>
                <span style={styles.timeLabel}>{getTigerResource("TIGER_RES_START")}</span>
                <span style={styles.timeValue}>{start.primary}</span>
                {start.secondary && <span style={styles.timeDate}>{start.secondary}</span>}
              </div>
              <div style={{ ...styles.timeCol, ...styles.timeColCenter }}>
                <span style={styles.timeLabel}>{getTigerResource("TIGER_CHANNEL_CUR")}</span>
                <span style={{ ...styles.timeValue, ...styles.timeValueCurrent }}>{current.primary}</span>
                {current.secondary && <span style={styles.timeDate}>{current.secondary}</span>}
              </div>
              <div style={{ ...styles.timeCol, ...styles.timeColRight }}>
                <span style={styles.timeLabel}>{getTigerResource("TIGER_RES_END")}</span>
                <span style={styles.timeValue}>{end.primary}</span>
                {end.secondary && <span style={styles.timeDate}>{end.secondary}</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button className="btn-acrylic-secondary" onClick={onCancel}>
            {getTigerResource("TIGER_RES_CANCEL") || "Cancel"}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  ...acrylicStyles,
  // Flex-centred overlay card (no fixed geometry), so the dialog keeps its own
  // centring mechanics and only the surface adopts the acrylic treatment.
  overlay: {
    ...acrylicStyles.overlay,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dialog: {
    background: "var(--ui-surface)",
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    border: "1px solid rgba(255,255,255,0.65)",
    borderRadius: 14,
    width: 440,
    maxWidth: "95vw",
    boxShadow: "0 16px 48px rgba(20,30,60,0.35)",
    overflow: "hidden" as const,
    display: "flex",
    flexDirection: "column" as const,
    fontFamily: acrylicTokens.font,
    color: acrylicTokens.textPrimary,
  },
  barOuter: {
    position: "relative" as const,
    height: 20,
    background: "rgba(255,255,255,0.85)",
    border: "1px solid rgba(120,130,150,0.35)",
    borderRadius: 7,
    overflow: "hidden",
  },
  barInner: {
    height: "100%",
    background: "var(--ui-btn-primary-bg)",
    transition: "width 0.2s ease",
  },
  barLabel: {
    position: "absolute" as const,
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 600,
    color: "#4b5260",
  },
  timeline: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 8,
    marginTop: 16,
  },
  timeCol: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "flex-start" as const,
    gap: 1,
  },
  timeColCenter: {
    alignItems: "center" as const,
    flex: 1,
  },
  timeColRight: {
    alignItems: "flex-end" as const,
  },
  timeLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: "uppercase" as const,
    color: acrylicTokens.textMuted,
  },
  timeValue: {
    fontSize: 13,
    fontVariantNumeric: "tabular-nums",
    color: acrylicTokens.textPrimary,
    marginTop: 2,
  },
  timeValueCurrent: {
    fontSize: 17,
    fontWeight: 700,
    color: acrylicTokens.accent,
    lineHeight: 1.1,
  },
  timeDate: {
    fontSize: 11,
    fontVariantNumeric: "tabular-nums",
    color: acrylicTokens.textSecondary,
  },
} as const;
